import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Box,
  Card,
  CircularProgress,
  Fade,
  IconButton,
  Modal,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IBusiness, IContact } from '@trii/types/dist/Contacts/contacts';
import { Spaces } from '@trii/types';
import { I18nextProvider } from 'react-i18next';
import { useEffect, useMemo, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

import BusinessSelectEditor from './components/BusinessSelect/BusinessSelectEditor';
import { BusinessMembersEditor, BusinessMembersSection } from './components/BusinessMembers';
import Header from './components/Header/Header';
import TagsEditor from './components/TagsEditor/TagsEditor';
import SearchLocationEditor from './components/SearchLocation/SearchLocationEditor';
import { AddressInformation, ContactInformation, General } from './components/Section';
import { EditContactApiProvider } from './context/EditContactApiContext';
import { EditContactViewProvider } from './context/EditContactViewContext';
import type { EditContactView } from './context/EditContactViewContext';
import { EditEntityProvider } from './context/EditEntityContext';
import { NavigateProvider } from './context/NavigateContext';
import { TranslationProvider } from './context/TranslationContext';
import { useEditContactModalController } from './hooks/useEditContactModalController';
import useImage from './hooks/useImage';
import { useEntityUpdateSync } from './hooks/useEntityUpdateSync';
import { useModalCloseHandler } from './hooks/useModalCloseHandler';
import { useTagsEditorController } from './hooks/useTagsEditorController';
import { createEditContactApi } from './services/api';
import i18nInstance from '../../i18n/config';

export type EditContactMode = 'modal' | 'card';

export interface EditContactModalProps {
  open?: boolean;
  onClose?: () => void;
  baseUrl?: string;
  spaceId?: string;
  contactId?: string;
  language?: 'en' | 'es';
  sx?: SxProps<Theme>;
  navigate: (path: string) => void;
  googleMapsApiKey?: string;
  onEntityUpdated?: (entity: IContact | IBusiness) => void;
  contactNameFormat?: Spaces.nameFormat;
  mode?: EditContactMode;
  title?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  entityType?: 'contact' | 'business';
  entityId?: string;
  initialEntityData?: IContact | IBusiness;
}

export type Props = EditContactModalProps;

type EditorContentProps = Required<Pick<EditContactModalProps, 'mode'>> &
  Pick<
    EditContactModalProps,
    | 'baseUrl'
    | 'spaceId'
    | 'contactId'
    | 'language'
    | 'googleMapsApiKey'
    | 'onEntityUpdated'
    | 'contactNameFormat'
    | 'entityType'
    | 'entityId'
    | 'initialEntityData'
  > & {
    open: boolean;
    contentRef: RefObject<HTMLDivElement>;
  };

type CardShellProps = {
  title?: string;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: ReactNode;
};

const getPanelBackground = (theme: Theme) =>
  (theme.palette.background as any).panel ?? theme.palette.background.paper;

const getAccordionHeaderBackground = (theme: Theme) =>
  (theme.palette.background as any).accordionHeader ??
  (theme.palette.background as any).panel ??
  theme.palette.background.paper;

const mergeSx = (baseSx: SxProps<Theme>, sx?: SxProps<Theme>): SxProps<Theme> => {
  if (Array.isArray(sx)) return [baseSx, ...sx] as SxProps<Theme>;
  if (sx) return [baseSx, sx] as SxProps<Theme>;
  return baseSx;
};

const EditContactCardShell = ({
  title = 'Details',
  expanded,
  onExpandedChange,
  children,
}: CardShellProps) => {
  const cardSx: SxProps<Theme> = {
    m: 2,
    border: '1px solid',
    borderColor: 'divider',
    overflow: 'hidden',
  };

  if (expanded !== undefined) {
    return (
      <Card sx={cardSx} id="details">
        <Accordion
          expanded={expanded}
          onChange={() => onExpandedChange?.(!expanded)}
          sx={{
            boxShadow: 'none',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box
              sx={{
                width: '100%',
                py: 1,
                px: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                color: (theme) => theme.palette.text.primary,
              }}
            >
              <Typography variant="subtitle2">{title}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2, borderTop: '1px solid rgba(0, 0, 0, .125)' }}>
            {children}
          </AccordionDetails>
        </Accordion>
      </Card>
    );
  }

  return (
    <Card sx={cardSx} id="details">
      <Box
        sx={{
          backgroundColor: getAccordionHeaderBackground,
          px: 2,
          py: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Card>
  );
};

const ViewPane = ({
  active,
  mode,
  children,
}: {
  active: boolean;
  mode: EditContactMode;
  children: ReactNode;
}) => {
  if (mode === 'card') {
    if (!active) return null;
    return (
      <Fade in timeout={200}>
        <Box sx={{ pt: 0.5 }}>{children}</Box>
      </Fade>
    );
  }

  return (
    <Fade in={active} timeout={200} mountOnEnter unmountOnExit>
      <Box sx={{ pt: 0.5, position: 'absolute', inset: 0, overflowY: 'auto' }}>{children}</Box>
    </Fade>
  );
};

const EditContactEditorContent = ({
  open,
  mode,
  baseUrl,
  spaceId,
  contactId,
  language,
  googleMapsApiKey,
  onEntityUpdated,
  contactNameFormat,
  entityType,
  entityId,
  initialEntityData,
  contentRef,
}: EditorContentProps) => {
  useEffect(() => {
    if (
      (window as any).google?.maps ||
      document.querySelector('script[src^="https://maps.googleapis.com/maps/api/js"]')
    ) {
      return;
    }

    if (!googleMapsApiKey) {
      console.warn('Google Maps API key is missing. The location search feature will not work.');
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      // Google Maps is intentionally kept loaded for other components.
    };
  }, [googleMapsApiKey]);

  const { state, selectors, actions } = useEditContactModalController({
    open,
    baseUrl,
    spaceId,
    contactId,
    contactNameFormat,
    entityType,
    entityId,
    initialEntityData,
    resetOnClose: mode === 'modal',
  });

  const handleEntityUpdated = useEntityUpdateSync({
    entity: state.contactData,
    setEntity: (next) => {
      actions.setContactData(next ?? undefined);
      if (next && onEntityUpdated) {
        onEntityUpdated(next);
      }
    },
  });

  const {
    view,
    setView,
    draftLabels,
    setDraftLabels,
    labelsOptions,
    isSaving: isSavingTags,
    isCreatingLabel,
    handleCreateLabel,
    handleSaveTags,
  } = useTagsEditorController({
    open,
    baseUrl,
    spaceId,
    contactData: state.contactData,
    contactType: selectors.contactType,
    labels: selectors.labels,
    selectedTags: selectors.tags,
    onTagsUpdated: (tags) => {
      if (!state.contactData) return;
      const updatedEntity = { ...(state.contactData as any), tags };
      actions.setContactData(updatedEntity);
      if (onEntityUpdated) {
        onEntityUpdated(updatedEntity);
      }
    },
  });

  const image = useImage({
    baseUrl,
    spaceId,
    entityType:
      selectors.contactType === 'business' || selectors.contactType === 'contact'
        ? selectors.contactType
        : undefined,
    entityId: state.contactData ? String((state.contactData as any).id) : undefined,
    initialImageUrl: state.contactData?.imageUrl,
    onEntityUpdated: handleEntityUpdated,
  });

  if (state.isLoading && !state.contactData) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 220,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (state.error) {
    return <Box sx={{ mb: 2 }}>Failed to load contact</Box>;
  }

  if (!state.contactData) {
    return null;
  }

  const normalizedEntityType: 'contact' | 'business' =
    selectors.contactType === 'business' ? 'business' : 'contact';

  const renderPane = (paneView: EditContactView, children: ReactNode) => (
    <ViewPane active={view === paneView} mode={mode}>
      {children}
    </ViewPane>
  );

  return (
    <EditContactViewProvider value={{ view, setView }}>
      <EditEntityProvider
        entityType={normalizedEntityType}
        entityData={state.contactData}
        customContactFields={selectors.contactFields}
        baseUrl={baseUrl}
        spaceId={spaceId}
        onEntityUpdated={handleEntityUpdated}
      >
        <Box sx={{ position: 'relative', height: mode === 'modal' ? '100%' : 'auto' }}>
          {renderPane(
            'main',
            <>
              {mode === 'modal' ? (
                <Header
                  imgUrl={image.imageUrl}
                  displayName={selectors.displayName}
                  tags={selectors.tags}
                  onEditTags={() => {
                    setView('tags');
                  }}
                  onUploadPhoto={image.action.uploadImage}
                  onPhotoFileChange={image.inputAtributes.onChange}
                  photoInputRef={image.inputAtributes.ref}
                  isUploadingPhoto={image.isUploading}
                  onDeletePhoto={image.action.deleteImage}
                  isDeletingPhoto={image.isDeleting}
                  contactType={selectors.contactType}
                  entityId={String((state.contactData as any).id)}
                  firstName={
                    selectors.isContact
                      ? String((state.contactData as any).firstName ?? '')
                      : undefined
                  }
                  lastName={
                    selectors.isContact
                      ? String((state.contactData as any).lastName ?? '')
                      : undefined
                  }
                  name={
                    selectors.isBusiness ? String((state.contactData as any).name ?? '') : undefined
                  }
                  onEntityUpdated={handleEntityUpdated}
                />
              ) : null}

              <Box sx={mode === 'modal' ? { px: 1, pb: 2 } : undefined}>
                <General
                  dateFormat={selectors.userTrii?.regCon_dateFormat || 'MM/dd/yyyy'}
                  entityType={normalizedEntityType}
                  entityData={state.contactData}
                  customContactFields={selectors.contactFields}
                  baseUrl={baseUrl}
                  spaceId={spaceId}
                  onEntityUpdated={handleEntityUpdated}
                  currentUser={selectors.userTrii}
                />

                {mode === 'card' && selectors.isBusiness ? (
                  <BusinessMembersSection
                    business={state.contactData as IBusiness}
                    onEdit={() => setView('businessMembers')}
                    language={language}
                    contactNameFormat={contactNameFormat}
                  />
                ) : null}

                <AddressInformation
                  entityType={normalizedEntityType}
                  entityData={state.contactData}
                  baseUrl={baseUrl}
                  spaceId={spaceId}
                  onEntityUpdated={handleEntityUpdated}
                  onDirectionSearchClick={() => setView('searchLocation')}
                />
                <ContactInformation ownerData={state.contactData} />
              </Box>
            </>,
          )}

          {renderPane(
            'searchLocation',
            <SearchLocationEditor
              baseUrl={baseUrl}
              spaceId={spaceId}
              entityType={normalizedEntityType}
              entityId={String((state.contactData as any).id)}
              onBack={() => setView('main')}
              onEntityUpdated={handleEntityUpdated}
            />,
          )}

          {renderPane(
            'tags',
            <TagsEditor
              value={draftLabels}
              options={labelsOptions}
              onChange={setDraftLabels}
              onBack={() => setView('main')}
              onSave={handleSaveTags}
              isSaving={isSavingTags}
              onCreateNew={handleCreateLabel}
              isCreatingNew={isCreatingLabel}
              popperContainer={contentRef.current}
            />,
          )}

          {renderPane(
            'businessSelect',
            <BusinessSelectEditor
              baseUrl={baseUrl}
              spaceId={spaceId}
              contactId={String((state.contactData as any).id)}
              initialBusiness={
                selectors.isContact && (state.contactData as any)?.businessId
                  ? {
                      businessId: String((state.contactData as any).businessId),
                      businessName: String((state.contactData as any).businessName ?? ''),
                    }
                  : null
              }
              onBack={() => setView('main')}
              onEntityUpdated={handleEntityUpdated}
              currentUser={selectors.userTrii}
            />,
          )}

          {renderPane(
            'businessMembers',
            <BusinessMembersEditor
              business={selectors.isBusiness ? (state.contactData as IBusiness) : null}
              onBack={() => setView('main')}
              onEntityUpdated={handleEntityUpdated}
            />,
          )}
        </Box>
      </EditEntityProvider>
    </EditContactViewProvider>
  );
};

const EditContactModalContent = ({
  mode = 'modal',
  open: openProp,
  onClose: onCloseProp,
  baseUrl,
  spaceId,
  contactId,
  sx,
  language = 'en',
  navigate,
  googleMapsApiKey,
  onEntityUpdated,
  contactNameFormat,
  title,
  expanded,
  onExpandedChange,
  entityType,
  entityId,
  initialEntityData,
}: EditContactModalProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const open = mode === 'card' ? true : Boolean(openProp);
  const onClose = onCloseProp ?? (() => {});

  const t = useMemo(() => {
    const fixedT = i18nInstance.getFixedT(language);
    return (key: string) => fixedT(key);
  }, [language]);

  const api = useMemo(() => {
    if (!baseUrl || !spaceId) return null;
    return createEditContactApi(baseUrl, spaceId);
  }, [baseUrl, spaceId]);

  const handleClose = useModalCloseHandler({ onClose });

  const editorContent = (
    <EditContactEditorContent
      mode={mode}
      open={open}
      baseUrl={baseUrl}
      spaceId={spaceId}
      contactId={contactId}
      language={language}
      googleMapsApiKey={googleMapsApiKey}
      onEntityUpdated={onEntityUpdated}
      contactNameFormat={contactNameFormat}
      entityType={entityType}
      entityId={entityId}
      initialEntityData={initialEntityData}
      contentRef={contentRef}
    />
  );

  const modalBaseSx: SxProps<Theme> = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'min(640px, calc(100vw - 32px))',
    height: 'min(560px, calc(100vh - 64px))',
    bgcolor: getPanelBackground,
    borderRadius: 3,
    boxShadow: 24,
    outline: 0,
    p: 3,
    overflow: 'visible',
  };

  const cardContentBaseSx: SxProps<Theme> = {
    minHeight: 0,
  };

  return (
    <EditContactApiProvider value={api}>
      <TranslationProvider t={t}>
        <NavigateProvider navigate={navigate}>
          {mode === 'card' ? (
            <EditContactCardShell
              title={title}
              expanded={expanded}
              onExpandedChange={onExpandedChange}
            >
              <Box ref={contentRef} sx={mergeSx(cardContentBaseSx, sx)}>
                {editorContent}
              </Box>
            </EditContactCardShell>
          ) : (
            <Modal
              open={open}
              onClose={handleClose}
              closeAfterTransition
              slots={{ backdrop: Backdrop }}
              slotProps={{ backdrop: { timeout: 200 } }}
            >
              <Fade in={open} timeout={200}>
                <Box sx={mergeSx(modalBaseSx, sx)} ref={contentRef}>
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                      position: 'absolute',
                      right: 5,
                      top: 5,
                      color: (theme) => theme.palette.grey[500],
                      zIndex: 1,
                    }}
                    size="small"
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                  {editorContent}
                </Box>
              </Fade>
            </Modal>
          )}
        </NavigateProvider>
      </TranslationProvider>
    </EditContactApiProvider>
  );
};

const EditContactModal = (props: EditContactModalProps) => {
  return (
    <I18nextProvider i18n={i18nInstance}>
      <EditContactModalContent {...props} />
    </I18nextProvider>
  );
};

export default EditContactModal;
